import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function MilestoneManagement() {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedStats, setSelectedStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    targetDeliveries: 10,
    periodType: 'daily',
    periodDays: 7,
    rewardAmount: 200,
    maxClaimsPerPartner: -1,
    isActive: true,
    validFrom: '',
    validUntil: ''
  });

  useEffect(() => { fetchMilestones(); }, []);

  const fetchMilestones = async () => {
    try {
      const res = await adminAPI.getMilestones();
      setMilestones(res.data.data || []);
    } catch (e) {
      console.error('Failed to fetch milestones:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.targetDeliveries || !form.rewardAmount) {
      alert('Name, target deliveries, and reward amount are required');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form,
        targetDeliveries: parseInt(form.targetDeliveries),
        rewardAmount: parseFloat(form.rewardAmount),
        periodDays: form.periodType === 'custom' ? parseInt(form.periodDays) : undefined,
        maxClaimsPerPartner: parseInt(form.maxClaimsPerPartner),
        validFrom: form.validFrom || undefined,
        validUntil: form.validUntil || undefined
      };
      if (editingId) {
        await adminAPI.updateMilestone(editingId, data);
      } else {
        await adminAPI.createMilestone(data);
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      await fetchMilestones();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (m) => {
    setForm({
      name: m.name,
      description: m.description || '',
      targetDeliveries: m.targetDeliveries,
      periodType: m.periodType,
      periodDays: m.periodDays || 7,
      rewardAmount: m.rewardAmount,
      maxClaimsPerPartner: m.maxClaimsPerPartner,
      isActive: m.isActive,
      validFrom: m.validFrom ? m.validFrom.split('T')[0] : '',
      validUntil: m.validUntil ? m.validUntil.split('T')[0] : ''
    });
    setEditingId(m._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this milestone? This cannot be undone.')) return;
    try {
      await adminAPI.deleteMilestone(id);
      await fetchMilestones();
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  const handleToggle = async (m) => {
    try {
      await adminAPI.updateMilestone(m._id, { isActive: !m.isActive });
      await fetchMilestones();
    } catch (e) {
      alert('Failed to toggle: ' + e.message);
    }
  };

  const handleViewStats = async (m) => {
    setStatsLoading(true);
    try {
      const res = await adminAPI.getMilestoneStats(m._id);
      setSelectedStats(res.data.data);
    } catch (e) {
      alert('Failed to load stats: ' + e.message);
    } finally {
      setStatsLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', targetDeliveries: 10, periodType: 'daily', periodDays: 7, rewardAmount: 200, maxClaimsPerPartner: -1, isActive: true, validFrom: '', validUntil: '' });
  };

  const periodLabels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', custom: 'Custom' };

  const styles = {
    container: { padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    title: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
    subtitle: { fontSize: '14px', color: '#666', marginTop: '4px' },
    btn: { padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    primaryBtn: { background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)', color: 'white' },
    card: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    activeBadge: { background: '#dcfce7', color: '#16a34a' },
    inactiveBadge: { background: '#fee2e2', color: '#dc2626' },
    rewardBadge: { background: '#fef3c7', color: '#d97706', padding: '4px 12px', borderRadius: '12px', fontSize: '14px', fontWeight: '700' },
    formOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    formCard: { background: 'white', borderRadius: '16px', padding: '32px', width: '500px', maxHeight: '90vh', overflow: 'auto' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' },
    select: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '4px', display: 'block' },
    fieldGroup: { marginBottom: '16px' },
    statsCard: { background: '#f8fafc', borderRadius: '8px', padding: '12px', marginTop: '8px' },
    actionBtn: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px', cursor: 'pointer', background: 'white', marginLeft: '6px' }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Milestone Rewards</div>
            <div style={styles.subtitle}>Configure delivery partner milestone bonuses</div>
          </div>
          <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}>
            + Create Milestone
          </button>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <div style={{ ...styles.card, flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Total Milestones</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e' }}>{milestones.length}</div>
          </div>
          <div style={{ ...styles.card, flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Active</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{milestones.filter(m => m.isActive).length}</div>
          </div>
          <div style={{ ...styles.card, flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Total Completions</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>{milestones.reduce((s, m) => s + (m.totalCompletions || 0), 0)}</div>
          </div>
          <div style={{ ...styles.card, flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Total Rewards Paid</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>Rs.{milestones.reduce((s, m) => s + (m.totalRewardsPaid || 0), 0)}</div>
          </div>
        </div>

        {/* Milestone List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading milestones...</div>
        ) : milestones.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏆</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>No milestones configured yet</div>
            <div style={{ fontSize: '14px', marginTop: '4px' }}>Create your first milestone to incentivize delivery partners</div>
          </div>
        ) : (
          milestones.map(m => (
            <div key={m._id} style={styles.card}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>{m.name}</span>
                  <span style={{ ...styles.badge, ...(m.isActive ? styles.activeBadge : styles.inactiveBadge) }}>
                    {m.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span style={{ ...styles.badge, background: '#ede9fe', color: '#7c3aed' }}>
                    {periodLabels[m.periodType] || m.periodType}{m.periodType === 'custom' ? ` (${m.periodDays}d)` : ''}
                  </span>
                </div>
                {m.description && <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>{m.description}</div>}
                <div style={{ fontSize: '13px', color: '#888' }}>
                  {m.targetDeliveries} deliveries = <span style={styles.rewardBadge}>Rs.{m.rewardAmount}</span>
                  {m.maxClaimsPerPartner > 0 && <span style={{ marginLeft: '8px', color: '#999' }}>(max {m.maxClaimsPerPartner}x per partner)</span>}
                  {m.maxClaimsPerPartner === -1 && <span style={{ marginLeft: '8px', color: '#999' }}>(unlimited)</span>}
                </div>
                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                  {m.totalCompletions || 0} completions | Rs.{m.totalRewardsPaid || 0} paid
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button style={styles.actionBtn} onClick={() => handleViewStats(m)}>Stats</button>
                <button style={styles.actionBtn} onClick={() => handleEdit(m)}>Edit</button>
                <button style={styles.actionBtn} onClick={() => handleToggle(m)}>
                  {m.isActive ? 'Disable' : 'Enable'}
                </button>
                <button style={{ ...styles.actionBtn, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => handleDelete(m._id)}>Delete</button>
              </div>
            </div>
          ))
        )}

        {/* Stats Modal */}
        {selectedStats && (
          <div style={styles.formOverlay} onClick={() => setSelectedStats(null)}>
            <div style={{ ...styles.formCard, width: '600px' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>"{selectedStats.milestone.name}" — Stats</h3>
                <button style={{ ...styles.actionBtn }} onClick={() => setSelectedStats(null)}>Close</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div style={styles.statsCard}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Target</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{selectedStats.milestone.targetDeliveries} deliveries</div>
                </div>
                <div style={styles.statsCard}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Total Completions</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#16a34a' }}>{selectedStats.milestone.totalCompletions}</div>
                </div>
                <div style={styles.statsCard}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Rewards Paid</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#d97706' }}>Rs.{selectedStats.milestone.totalRewardsPaid}</div>
                </div>
              </div>
              <h4 style={{ marginBottom: '8px' }}>Partners who completed</h4>
              {selectedStats.completions.length === 0 ? (
                <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No completions yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Partner</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Phone</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Times Claimed</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Last Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStats.completions.map((c, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px' }}>{c.name}</td>
                        <td style={{ padding: '8px' }}>{c.phone}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{c.totalClaimed}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{c.lastClaimedAt ? new Date(c.lastClaimedAt).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Create/Edit Form Modal */}
        {showForm && (
          <div style={styles.formOverlay} onClick={() => { setShowForm(false); setEditingId(null); }}>
            <div style={styles.formCard} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0' }}>{editingId ? 'Edit Milestone' : 'Create Milestone'}</h3>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Name *</label>
                <input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Daily Sprint" />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Description</label>
                <input style={styles.input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Complete deliveries to earn bonus" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Target Deliveries *</label>
                  <input style={styles.input} type="number" min="1" value={form.targetDeliveries} onChange={e => setForm({ ...form, targetDeliveries: e.target.value })} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Reward Amount (Rs.) *</label>
                  <input style={styles.input} type="number" min="1" value={form.rewardAmount} onChange={e => setForm({ ...form, rewardAmount: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Period Type *</label>
                  <select style={styles.select} value={form.periodType} onChange={e => setForm({ ...form, periodType: e.target.value })}>
                    <option value="daily">Daily (resets midnight)</option>
                    <option value="weekly">Weekly (resets Monday)</option>
                    <option value="monthly">Monthly (resets 1st)</option>
                    <option value="custom">Custom (N days)</option>
                  </select>
                </div>
                {form.periodType === 'custom' && (
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Period Days</label>
                    <input style={styles.input} type="number" min="1" value={form.periodDays} onChange={e => setForm({ ...form, periodDays: e.target.value })} />
                  </div>
                )}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Max Claims Per Partner</label>
                  <select style={styles.select} value={form.maxClaimsPerPartner} onChange={e => setForm({ ...form, maxClaimsPerPartner: parseInt(e.target.value) })}>
                    <option value="-1">Unlimited (each period)</option>
                    <option value="1">One-time only</option>
                    <option value="2">2 times</option>
                    <option value="3">3 times</option>
                    <option value="5">5 times</option>
                    <option value="10">10 times</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Valid From (optional)</label>
                  <input style={styles.input} type="date" value={form.validFrom} onChange={e => setForm({ ...form, validFrom: e.target.value })} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Valid Until (optional)</label>
                  <input style={styles.input} type="date" value={form.validUntil} onChange={e => setForm({ ...form, validUntil: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <button style={{ ...styles.btn, background: '#f0f0f0', color: '#333' }} onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
                <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default MilestoneManagement;
